import React from 'react'
import { graphql } from 'react-apollo'
import { Redirect, Route, RouteProps } from 'react-router'
import { branch, compose, renderComponent } from 'recompose'
import CenteredSpinner from 'components/CenteredSpinner'
import { User } from 'generated/types'
import { withApolloErrorHandler } from 'utils/Apollo'
import Environment, { withEnv } from 'utils/environment'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { VALID_FLOWS } from 'utils/FlowProvider/utils'

import userQuery from './gql/user.query.gql'
import { USER_UUID } from 'app-constants'
import { getWindow } from '@acorns/web-utils'

type Props = RouteProps

type InnerProps = {
  env: Environment
} & WithFlowRouterProps

type AddedProps = {
  redirectTo?: string
}

type EnhancedProps = Props & AddedProps

type GqlProps = {
  user: User
}

const enhance = compose<AddedProps, Props>(
  withEnv,
  withFlowRouter,
  withApolloErrorHandler('RegistrationCheck', () => () => false),
  graphql<InnerProps, GqlProps, {}, {}>(userQuery, {
    options: {
      fetchPolicy: 'network-only',
    },
    props: ({ data, ownProps: { flow, routeIfInvalidPage, env } }) => {
      if (data.loading) {
        return {
          loading: true,
        }
      }

      // Redirect to root page for all errors (unauthenticated error) or no user is returned
      if (data.error || !data.user) {
        return { loading: false, redirectTo: FlowRoute.Signup }
      }

      // If the user has completed registration and the fetched UUID does not match the
      // existing session UUID (and a session UUID exists), redirect them to the main app
      const sessionUuid = window.sessionStorage.getItem(USER_UUID)
      if (data.user.registrationComplete && sessionUuid && data.user.uuid !== sessionUuid) {
        getWindow().location.replace(env.get('webAppUrl'))
        return { loading: false }
      }

      // Users could attempt to go into an invalid flow or have stale values in local storage
      // Routes to welcome page to reset flow value through resume flow
      if (!VALID_FLOWS.includes(flow)) {
        return { loading: false, redirectTo: FlowRoute.Welcome }
      }

      routeIfInvalidPage()
    },
  }),
  branch(({ loading }) => loading, renderComponent(CenteredSpinner)),
)

const Component = (props: EnhancedProps) => {
  return props.redirectTo ? (
    <Redirect to={props.redirectTo} />
  ) : (
    <Route path={props.path} component={props.component} />
  )
}

export const PrivateRoute = enhance(Component)
